import { Component, OnInit, OnDestroy, Inject, ViewChild, ElementRef, HostListener, AfterViewInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ReportsService } from '@app-reports/services/reports/reports.service';
import { CustomReport, CustomReportsResponse, Metadata, ReportDomain, ResolutionCandidate } from '@app-reports/models/reports.model';
import { API } from '@app-core/constants/api.constants';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DurationRange } from '@app-core/models/core.model';
import { Store } from '@ngrx/store';
import { getDurationFilterState } from '@app-home/reducers';
import { TALK_TO_DATA_DATE_RANGES, TALK_TO_DATA_PREDEFINED_QUESTIONS } from '@app-reports/constants/reports.constants';
import { TranslateService } from '@ngx-translate/core';
import { AccessService } from '@app-core/services/access/access.service';
import { TaggingService } from '@app-asset-config/services/tagging.service';
import { DataService } from '@app-core/services/data/data.service';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';
import { marked } from 'marked';

interface ChatMessage {
  type: 'user' | 'bot' | 'sql' | 'insights' | 'resolution';
  content: string;
  timestamp: Date;
  isPeriodSelection?: boolean;
  sqlData?: string;
  showSqlExpanded?: boolean;
  renderedContent?: SafeHtml;
  candidates?: ResolutionCandidate[];
}

interface Tag {
  tagId: number;
  tagName: string;
  attributeId?: number;
  attributeName?: string;
  attributeType?: string;
  id?: number;
  name?: string;
}

interface ReportDataRow {
  [key: string]: string | number | boolean | null | undefined;
}

type ConversationState = 'initial' | 'awaiting_period' | 'report_generated';

@Component({
  selector: 'app-talk-to-data-modal',
  templateUrl: './talk-to-data-modal.component.html',
  styleUrls: ['./talk-to-data-modal.component.scss'],
})
export class TalkToDataModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chatContainer') private chatContainer: ElementRef;

  public tripDetailsForm: FormGroup;
  public durationControl: FormControl;
  public chatMessages: ChatMessage[] = [];
  public conversationState: ConversationState = 'initial';
  public isLoading = false;
  public isFirstQuery = true;
  public predefinedQuestions: string[] = TALK_TO_DATA_PREDEFINED_QUESTIONS;
  public modalTitle = 'Custom Report';
  public dataSource: ReportDataRow[] = [];
  public displayedColumns: string[] = [];
  public showNoDataMessage = true;
  public showNoDataRetrySection = false;
  public currentQuestion = '';
  public currentAnswer = '';
  public sessionId = '';
  public sql: string[] = [];
  public executedSql: string[] = [];
  public sql_statements: string[] = [];
  public metadata: Metadata = { date_range: '', sql_where_date_columns: [], tag_ids: [], domain: 'trip' };
  public reportPeriods = TALK_TO_DATA_DATE_RANGES;
  public selectedPeriod = 'Past 7 days';
  public durationRange: DurationRange;
  public unSelectAllTag = false;
  public entityType = ['asset', 'driver'];
  public userTags: Tag[] = [];
  public assignedTagList: number[] = [];
  public selectedTagIds: number[] = [];
  public filterSelectedTagIds: number[] = [];
  public selectedTagNames: string[] = [];
  public showSaveReportModal = false;
  public csvLoader = false;
  public saveReportLoader = false;
  public reportName = '';
  public reportDescription = '';
  public isGeneratingInsights = false;
  public showInsightsModal = false;
  public insightsAlreadyGenerated = false;
  public visualsAlreadyGenerated = false;
  public isEditMode = false;
  public editReport: CustomReport | null = null;
  public customReportId = '';
  public reportQuery: string = '';
  public selectedTabIndex = 0;
  public userProfileIcon = '';
  public getCustomReportsListData: () => void;
  private clientId = '';
  private fleetId = '';
  private allTags: Tag[] = [];
  private attributeList: any[] = [];
  private lastUsedPeriod = '';
  private lastUsedTagIds: number[] = [];
  private lastUsedTagNames: string[] = [];
  private pendingTagSelection: number[] | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private reportService: ReportsService,
    private snackBarService: SnackBarService,
    private translate: TranslateService,
    private accessService: AccessService,
    private taggingService: TaggingService,
    private dialogRef: MatDialogRef<TalkToDataModalComponent>,
    public dataService: DataService,
    public durationService: DurationRangeService,
    private sanitizer: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<any>
  ) {}

  ngOnInit(): void {
    const { loginName = '', customerName = '' } = this.accessService.getLoginInfo();

    this.clientId = this.data?.clientId || customerName;
    this.fleetId = this.data?.fleetId;
    this.userProfileIcon = loginName.slice(0, 2);
    this.getCustomReportsListData = this.data?.getCustomReportsListData;
    this.isEditMode = this.data?.isEditMode ?? false;
    this.editReport = this.data?.editReport ?? null;
    this.customReportId = this.data?.customReportId ?? '';
    this.sessionId = this.data?.sessionId ?? '';
    this.reportQuery = this.data?.reportQuery ?? '';
    this.sql = this.data?.sql ?? [];
    this.reportName = this.editReport?.reportName ?? '';
    this.reportDescription = this.editReport?.reportDescription ?? '';
    this.modalTitle = this.isEditMode ? 'What would you like to edit?' : 'Hi! How can I help you?';

    this.durationControl = new FormControl(null);
    this.tripDetailsForm = this.fb.group({
      customQuestion: [''],
    });

    this.calculateDateRange('Past 7 days');

    this.store
      .select(getDurationFilterState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((durationFilter: any) => {
        const { days, displayStartDate: startDate, displayEndDate: endDate } = durationFilter;
        this.durationRange = { days, startDate, endDate };
      });

    this.loadAttributeList();

    if (this.isEditMode && this.editReport) {
      this.initializeEditMode();
    } else {
      this.addBotMessage(this.translate.instant('You can select a predefined question or type your own.'));
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.scrollToBottom(), 100);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.date-range-wrapper')) {
    }
  }

  public isAskButtonDisabled(): boolean {
    const question = this.tripDetailsForm.get('customQuestion')?.value?.trim();
    if (this.isLoading) return true;
    if (!question) return true;
    if (this.isFirstQuery && this.conversationState === 'awaiting_period') return true;
    return false;
  }

  public onTabChange(event: any): void {
    this.selectedTabIndex = event.index;
  }

  public toggleSqlThinkingForMessage(index: number): void {
    const msg = this.chatMessages[index];
    if (msg?.type === 'sql') {
      msg.showSqlExpanded = !msg.showSqlExpanded;
    }
  }

  public onPredefinedQuestionClick(question: string): void {
    if (!this.isFirstQuery || this.conversationState !== 'initial') return;

    this.currentQuestion = question;
    this.addUserMessage(question);
    this.conversationState = 'awaiting_period';
    this.addBotMessage(this.translate.instant('Just to confirm before generating the report please select a period and related tags:'), {
      isPeriodSelection: true,
    });
  }

  public onConfirmPeriodAndTags(): void {
    if (!this.selectedPeriod) {
      this.snackBarService.failure(this.translate.instant('Please select a period'));
      return;
    }
    if (this.conversationState !== 'awaiting_period') return;

    const tagsToUse = this.filterSelectedTagIds.length > 0 ? this.filterSelectedTagIds : this.selectedTagIds;
    this.selectedTagIds = [...tagsToUse];
    this.selectedTagNames = this.getTagNamesByIds(this.selectedTagIds);
    this.userTags = this.getTagObjectsByIds(this.selectedTagIds);

    const tagText = this.selectedTagNames.length > 0 ? this.selectedTagNames.join(', ') : this.translate.instant('No tags selected');
    this.addUserMessage(`${this.selectedPeriod}, ${tagText}`);
    this.conversationState = 'report_generated';
    this.executeQuery(this.currentQuestion);
  }

  public onRetryWithNewFilters(): void {
    if (!this.selectedPeriod && this.lastUsedPeriod) {
      this.selectedPeriod = this.lastUsedPeriod;
    }
    if (!this.selectedPeriod) {
      this.snackBarService.failure(this.translate.instant('Please select a period'));
      return;
    }

    if (this.filterSelectedTagIds.length > 0) {
      this.selectedTagIds = [...this.filterSelectedTagIds];
    }
    this.selectedTagNames = this.getTagNamesByIds(this.selectedTagIds);
    this.userTags = this.getTagObjectsByIds(this.selectedTagIds);

    const tagText = this.selectedTagNames.length > 0 ? this.selectedTagNames.join(', ') : this.translate.instant('No tags selected');
    this.addUserMessage(`${this.selectedPeriod}, ${tagText}`);
    this.showNoDataRetrySection = false;
    this.executeQuery(this.getEffectiveQuestion());
  }

  public onCustomQuestionSubmit(): void {
    const typedQuestion = this.tripDetailsForm.get('customQuestion')?.value?.trim();

    if (this.isFirstQuery && this.conversationState === 'initial') {
      if (!typedQuestion) {
        this.snackBarService.failure(this.translate.instant('Please enter a question'));
        return;
      }
      this.currentQuestion = typedQuestion;
      this.addUserMessage(typedQuestion);
      this.conversationState = 'awaiting_period';
      this.addBotMessage(
        this.translate.instant('Just to confirm before generating the report preview, what period would you like for this report?'),
        { isPeriodSelection: true }
      );
      this.clearCustomQuestionInput();
      return;
    }

    if (this.isFirstQuery && this.conversationState === 'awaiting_period') {
      if (!typedQuestion) {
        this.snackBarService.failure(this.translate.instant('Please select a period and click confirm'));
        return;
      }
      this.addUserMessage(typedQuestion);
      this.addBotMessage(this.translate.instant('Please select a period from the dropdown and click confirm to continue.'));
      this.clearCustomQuestionInput();
      return;
    }

    if (!this.isFirstQuery && this.conversationState === 'report_generated') {
      if (!typedQuestion) {
        this.snackBarService.failure(this.translate.instant('Please enter a question'));
        return;
      }
      if (!this.selectedPeriod) {
        this.snackBarService.failure(this.translate.instant('Please ensure period is selected'));
        return;
      }
      this.currentQuestion = typedQuestion;
      this.addUserMessage(typedQuestion);
      this.executeQuery(typedQuestion);
      this.clearCustomQuestionInput();
    }
  }

  public selectDateRangeFromFilter(days: string): void {
    this.selectedPeriod = days;
    this.visualsAlreadyGenerated = false;
    this.insightsAlreadyGenerated = false;
    this.executeQuery(this.getEffectiveQuestion());
  }

  public selectedTags(tags: number[]): void {
    this.selectedTagIds = tags;
    this.filterSelectedTagIds = tags;
    this.assignedTagList = tags;
    this.selectedTagNames = this.getTagNamesByIds(tags);
    this.visualsAlreadyGenerated = false;
    this.insightsAlreadyGenerated = false;
  }

  public selectedTagsRightSection(tags: number[]): void {
    this.visualsAlreadyGenerated = false;
    this.insightsAlreadyGenerated = false;
    this.pendingTagSelection = [...tags];
    this.filterSelectedTagIds = [...tags];
    this.selectedTagNames = this.getTagNamesByIds(tags);
  }

  public onTagDropdownClosed(): void {
    if (this.pendingTagSelection === null) return;

    const newTagIds = [...this.pendingTagSelection];
    const previousTagSet = new Set(this.selectedTagIds);
    const tagsChanged = previousTagSet.size !== newTagIds.length || newTagIds.some((id) => !previousTagSet.has(id));
    const questionToUse = this.getEffectiveQuestion();

    if (tagsChanged && questionToUse && this.conversationState === 'report_generated') {
      this.selectedTagIds = [...newTagIds];
      this.assignedTagList = [...newTagIds];
      this.selectedTagNames = this.getTagNamesByIds(newTagIds);
      this.userTags = this.getTagObjectsByIds(newTagIds);

      if (newTagIds.length === 0) {
        this.unSelectAllTag = true;
        setTimeout(() => {
          this.unSelectAllTag = false;
        }, 100);
      }

      this.currentQuestion = questionToUse;
      this.executeQuery(questionToUse);
    }

    this.pendingTagSelection = null;
  }

  public onGenerateInsights(): void {
    if (this.isGeneratingInsights) return;
    if (this.isWorkflowAwaitingInput) return;
    if (!this.dataSource?.length) {
      this.snackBarService.failure(this.translate.instant('No data available to generate insights'));
      return;
    }
    this.showInsightsModal = true;
    this.insightsAlreadyGenerated = true;
  }

  public onGenerateVisuals(): void {
    this.selectedTabIndex = 1;
    this.visualsAlreadyGenerated = true;
  }

  public onInsightsModalCancel(): void {
    this.showInsightsModal = false;
  }

  public onInsightsModalProceed(): void {
    this.isGeneratingInsights = true;
    const sqlData = this.getSqlPayloadForLlm(this.getCurrentSqlStatements());

    const requestBody = {
      clientId: this.clientId,
      sql: sqlData,
      domain: this.getCurrentDomain(),
      ...(this.metadata?.crossDomainPlan ? { crossDomainPlan: this.metadata.crossDomainPlan } : {}),
      fleetId: this.fleetId,
      tagIds: this.selectedTagIds.map(Number),
      sessionId: this.sessionId,
    };

    this.reportService
      .generateInsights(requestBody)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isGeneratingInsights = false;
          this.showInsightsModal = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          const insightsText = response?.insights || response?.answer || '';
          if (insightsText) {
            this.addInsightsMessage(insightsText);
            this.insightsAlreadyGenerated = true;
            this.snackBarService.success(this.translate.instant('Insights generated successfully'));
          } else {
            this.addBotMessage(this.translate.instant('Unable to generate insights at this time. Please try again.'));
          }
        },
        error: () => {
          this.addBotMessage(this.translate.instant('Failed to generate insights. Please try again.'));
          this.snackBarService.failure(this.translate.instant('Failed to generate insights'));
        },
      });
  }

  public downloadReport(): void {
    if (this.isActionControlsDisabled) {
      return;
    }

    this.csvLoader = true;
    const sql = this.getSqlPayloadForLlm(this.getCurrentSqlStatements());
    const requestBody: any = {
      ...(this.sessionId && { sessionId: this.sessionId }),
      sql,
      domain: this.getCurrentDomain(),
      ...(this.getCurrentCrossDomainPlan() ? { crossDomainPlan: this.getCurrentCrossDomainPlan() } : {}),
    };
    this.http
      .post(API.POST_GENERATE_REPORT_NAME, requestBody)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const reportName = response?.report_name || '';
          this.downloadReportWithName(reportName);
        },
        error: () => {
          this.csvLoader = false;
          this.snackBarService.failure(this.translate.instant('Failed to generate report name. Please try again.'));
        },
      });
  }

  public openSaveReportModal(): void {
    if (this.isActionControlsDisabled) {
      return;
    }

    if (!this.dataSource.length) {
      this.snackBarService.failure(this.translate.instant('No data available to save'));
      return;
    }

    if (this.isEditMode && this.editReport) {
      this.reportName = (this.editReport as CustomReport).reportName ?? '';
      this.reportDescription = (this.editReport as CustomReport).reportDescription ?? '';
      this.showSaveReportModal = true;
      return;
    }

    this.saveReportLoader = true;

    const requestBody = {
      ...(this.sessionId && { sessionId: this.sessionId }),
      ...(this.getCurrentSqlStatements().length && { sql: this.getSqlPayloadForLlm(this.getCurrentSqlStatements()) }),
      domain: this.getCurrentDomain(),
      ...(this.getCurrentCrossDomainPlan() ? { crossDomainPlan: this.getCurrentCrossDomainPlan() } : {}),
    };

    this.http
      .post(API.POST_GENERATE_REPORT_NAME, requestBody)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.saveReportLoader = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          this.reportName = response.report_name ?? '';
          const today = new Date();
          const formatted = `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(
            2,
            '0'
          )}.${today.getFullYear()}`;
          const tagText = this.selectedTagNames.length > 0 ? this.selectedTagNames.join(', ') : 'No tags';
          this.reportDescription = `${response.report_description ?? ''} Created on ${formatted} with ${tagText}.`;
          this.showSaveReportModal = true;
        },
        error: () => {
          this.snackBarService.failure(this.translate.instant('Failed to generate report name. Please try again.'));
        },
      });
  }

  public onReportSaved(): void {
    this.showSaveReportModal = false;
    this.dialogRef.close();
  }

  public startNewChat(): void {
    this.selectedTabIndex = 0;
    this.currentQuestion = '';
    this.currentAnswer = '';
    this.sessionId = '';
    this.selectedPeriod = 'Past 7 days';
    this.conversationState = 'initial';
    this.chatMessages = [];
    this.isFirstQuery = true;
    this.showNoDataMessage = true;
    this.showNoDataRetrySection = false;
    this.dataSource = [];
    this.displayedColumns = [];
    this.sql = [];
    this.executedSql = [];
    this.sql_statements = [];
    this.metadata = { date_range: '', sql_where_date_columns: [], tag_ids: [], domain: 'trip' };
    this.showSaveReportModal = false;
    this.lastUsedPeriod = '';
    this.lastUsedTagIds = [];
    this.lastUsedTagNames = [];
    this.selectedTagIds = [];
    this.filterSelectedTagIds = [];
    this.selectedTagNames = [];
    this.assignedTagList = [];
    this.isGeneratingInsights = false;
    this.showInsightsModal = false;
    this.insightsAlreadyGenerated = false;
    this.visualsAlreadyGenerated = false;
    this.isEditMode = false;
    this.editReport = null;
    this.customReportId = '';
    this.reportQuery = '';
    this.modalTitle = 'Hi! How can I help you?';
    this.pendingTagSelection = null;

    this.tripDetailsForm?.reset();
    this.durationControl?.setValue(null);
    this.unSelectAllTag = true;

    setTimeout(() => {
      this.initializePreAssignedTags();
      this.unSelectAllTag = false;
    }, 100);

    this.calculateDateRange('Past 7 days');
    this.addBotMessage(this.translate.instant('You can select a predefined question or type your own.'));
  }

  private clearCustomQuestionInput(): void {
    this.tripDetailsForm.get('customQuestion')?.setValue('');
  }

  private scrollToBottom(): void {
    try {
      if (this.chatContainer) {
        this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      console.error('Scroll error:', err);
    }
  }

  private addUserMessage(content: string): void {
    this.chatMessages.push({ type: 'user', content, timestamp: new Date() });
    setTimeout(() => this.scrollToBottom(), 100);
  }

  private addBotMessage(content: string, options?: { isPeriodSelection?: boolean }): void {
    const html = marked.parse(content) as string;
    const renderedContent = this.sanitizer.bypassSecurityTrustHtml(html);
    this.chatMessages.push({ type: 'bot', content, renderedContent, timestamp: new Date(), ...options });
    setTimeout(() => this.scrollToBottom(), 100);
  }

  public onCandidateSelect(candidate: ResolutionCandidate): void {
    const label = candidate.asset_name || candidate.assetid;
    this.addUserMessage(label);
    this.executeQuery(label);
  }

  private addInsightsMessage(content: string): void {
    const html = marked.parse(content) as string;
    const safeHtml = this.sanitizer.bypassSecurityTrustHtml(html);
    this.chatMessages.push({ type: 'insights', content, renderedContent: safeHtml, timestamp: new Date() });
  }

  private addSqlThinkingMessage(sqlData: string): void {
    this.chatMessages.push({
      type: 'sql',
      content: '',
      timestamp: new Date(),
      sqlData,
      showSqlExpanded: false,
    });
    setTimeout(() => this.scrollToBottom(), 100);
  }

  private getEffectiveQuestion(): string {
    return this.currentQuestion?.trim() || this.editReport?.reportPayload?.question?.trim() || '';
  }

  private calculateDateRange(range: string): void {
    const today = new Date();
    const daysMap: Record<string, number> = {
      'Past 3 days': 3,
      'Past 7 days': 7,
      'Past 14 days': 14,
      'Past 30 days': 30,
    };
    const offset = daysMap[range] ?? 30;
    today.setDate(today.getDate() - offset);
  }

  private loadAttributeList(): void {
    this.taggingService
      .getAttributes({ limit: 100, offset: 0 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.attributeList = res?.data ?? [];
          this.allTags = this.extractAllTags(this.attributeList);
          this.initializePreAssignedTags();
        },
        error: () => {},
      });
  }

  private extractAllTags(attributes: any[]): Tag[] {
    if (!Array.isArray(attributes)) return [];
    return attributes.flatMap((attr) =>
      (attr.tags ?? []).map((tag: any) => ({
        ...tag,
        attributeId: attr.attributeId,
        attributeName: attr.attributeName,
        attributeType: attr.attributeType,
      }))
    );
  }

  private getTagNamesByIds(tagIds: number[]): string[] {
    if (!tagIds?.length) return [];
    return tagIds.reduce<string[]>((acc, tagId) => {
      const found = this.allTags.find((t) => t.tagId === tagId) ?? this.userTags.find((t) => t.id === tagId || t.tagId === tagId);
      const name = found?.tagName ?? (found as any)?.name;
      if (name) acc.push(name);
      return acc;
    }, []);
  }

  private getTagObjectsByIds(tagIds: number[]): Tag[] {
    if (!tagIds?.length) return [];
    return tagIds.reduce<Tag[]>((acc, tagId) => {
      const fromAll = this.allTags.find((t) => t.tagId === tagId);
      if (fromAll) {
        acc.push(fromAll);
        return acc;
      }

      const fromUser = this.userTags.find((t) => t.id === tagId || t.tagId === tagId);
      if (fromUser) {
        acc.push({
          tagId: fromUser.id ?? fromUser.tagId,
          tagName: (fromUser as any).name ?? fromUser.tagName,
          ...fromUser,
        });
      }
      return acc;
    }, []);
  }

  private initializePreAssignedTags(): void {
    const { fleets = [] } = this.accessService.getLoginInfo();
    const fleet = fleets.find((x: any) => x.fleetId === this.fleetId);
    const preAssigned: number[] = fleet?.userTags?.map((t: any) => t.tagId) ?? [];

    this.selectedTagIds = [...preAssigned];
    this.filterSelectedTagIds = [...preAssigned];
    this.lastUsedTagIds = [...preAssigned];
    this.selectedTagNames = this.getTagNamesByIds(preAssigned);
    this.lastUsedTagNames = [...this.selectedTagNames];
    this.userTags = this.getTagObjectsByIds(preAssigned);
    this.assignedTagList = [...preAssigned];
  }

  private executeQuery(question: string): void {
    if (!this.selectedPeriod) {
      this.snackBarService.failure(this.translate.instant('Please select a report period'));
      return;
    }

    this.lastUsedPeriod = this.selectedPeriod;
    this.lastUsedTagIds = [...this.selectedTagIds];
    this.lastUsedTagNames = [...this.selectedTagNames];
    this.isLoading = true;
    this.showNoDataMessage = false;
    this.showNoDataRetrySection = false;
    this.insightsAlreadyGenerated = false;
    this.dataSource = [];
    this.displayedColumns = [];

    const requestBody: any = {
      question: question,
      clientId: this.clientId,
      fleetId: this.fleetId,
      dateRange: this.selectedPeriod,
      tagIds: this.selectedTagIds,
      ...(this.isEditMode && { sql: this.getSqlPayloadForLlm(this.toSqlArray(this.data?.sql)) }),
      ...(this.sessionId && { sessionId: this.sessionId }),
    };

    this.reportService
      .getCustomReports(requestBody)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (response: CustomReportsResponse) => {
          if (response?.sessionId) this.sessionId = response.sessionId;

          this.sql = response?.sql_statements ?? [];
          this.executedSql = response?.executed_sql_statements ?? [];
          this.sql_statements = response?.sql_statements ?? [];
          const responseMetadata = response?.metadata as Partial<Metadata> | undefined;
          this.metadata = responseMetadata
            ? {
                ...responseMetadata,
                date_range: responseMetadata.date_range ?? '',
                sql_where_date_columns: responseMetadata.sql_where_date_columns ?? [],
                tag_ids: responseMetadata.tag_ids ?? [],
                domain: responseMetadata.domain ?? 'trip',
              }
            : { date_range: '', sql_where_date_columns: [], tag_ids: [], domain: 'trip' };

          if (response.sql_statements?.length) {
            const tqi = (response.metadata as Metadata)?.tableQueryIndex ?? 0;
            const sqlToShow = response.sql_statements[tqi] ?? response.sql_statements[0];
            this.addSqlThinkingMessage(sqlToShow);
          }
          this.processResponse(response);
          this.isFirstQuery = false;
          this.conversationState = 'report_generated';
        },
        error: (err) => {
          this.handleError(err);
          this.isFirstQuery = false;
          this.conversationState = 'report_generated';
        },
      });
  }

  private processResponse(response: CustomReportsResponse): void {
    if (!response) {
      this.handleNoData();
      return;
    }

    if (response.sessionId) this.sessionId = response.sessionId;

    if (response.needsResolution && response.candidates?.length) {
      const html = marked.parse(response.answer ?? '') as string;
      const safeHtml = this.sanitizer.bypassSecurityTrustHtml(html);
      this.chatMessages.push({
        type: 'resolution',
        content: response.answer ?? '',
        renderedContent: safeHtml,
        candidates: response.candidates,
        timestamp: new Date(),
      });
      setTimeout(() => this.scrollToBottom(), 100);
      return;
    }

    this.sql = response.sql_statements ?? [];
    this.currentAnswer = response.answer ?? '';
    this.addBotMessage(this.currentAnswer);

    if (this.isWorkflowAwaitingInput) {
      this.showNoDataRetrySection = false;
      this.showNoDataMessage = false;
      this.dataSource = [];
      this.displayedColumns = [];
      return;
    }

    const tqi = this.metadata?.tableQueryIndex ?? 0;
    const tableData = response.data?.[tqi];
    if (Array.isArray(tableData) && tableData.length > 0 && this.checkIfMeaningfulData(tableData)) {
      const processed = tableData.map((row) => {
        const r = { ...row };
        const key = 'Average Trip Duration (sec)';
        if (key in r && typeof r[key] === 'number') {
          r[key] = Math.round((r[key] as number) * 100) / 100;
        }
        return r;
      });

      this.displayedColumns = Object.keys(processed[0]);
      this.dataSource = processed;
      this.showNoDataMessage = false;
      this.showNoDataRetrySection = false;

      if (!this.isGeneratingInsights) {
        this.snackBarService.success(this.translate.instant('Report generated successfully'));
        this.addBotMessage(
          this.translate.instant('Report generated! You can view the preview on the right panel. Feel free to ask another question.')
        );
      }
    } else {
      this.handleNoData();
    }
  }

  private checkIfMeaningfulData(tableData: any[]): boolean {
    if (tableData.length > 1) return true;
    return Object.values(tableData[0]).some((v) => v !== null && v !== undefined && v !== '' && v !== 0);
  }

  private handleNoData(): void {
    if (this.isWorkflowAwaitingInput) {
      this.showNoDataRetrySection = false;
      this.showNoDataMessage = false;
      return;
    }

    this.showNoDataMessage = true;
    this.showNoDataRetrySection = true;
    this.dataSource = [];
    this.displayedColumns = [];
    this.restoreLastUsedFilters();
    this.addBotMessage(
      this.translate.instant('Currently there is no activity found for the selected values, kindly select a different value and try again:')
    );
  }

  private handleError(error: HttpErrorResponse): void {
    this.showNoDataMessage = true;
    this.showNoDataRetrySection = true;
    this.dataSource = [];
    this.displayedColumns = [];
    this.restoreLastUsedFilters();

    const statusMessages: Record<number, string> = {
      400: 'Invalid request. Please check your query and try again.',
      401: 'Unauthorized. Please log in again.',
      404: 'Service not found. Please contact support.',
      500: 'Unable to fetch data.',
    };
    const msg = statusMessages[error.status] ?? error.error?.message ?? 'Failed to generate report. Please try again.';

    this.addBotMessage(this.translate.instant(msg));
    this.snackBarService.failure(this.translate.instant(msg));
  }

  private restoreLastUsedFilters(): void {
    if (this.lastUsedTagIds.length > 0) {
      this.selectedTagIds = [...this.lastUsedTagIds];
      this.assignedTagList = this.getTagObjectsByIds(this.lastUsedTagIds) as any;
      this.selectedTagNames = [...this.lastUsedTagNames];
      this.userTags = this.getTagObjectsByIds(this.lastUsedTagIds);
    } else {
      this.selectedTagIds = [];
      this.selectedTagNames = [];
      this.assignedTagList = [];
      this.userTags = [];
    }
    if (this.lastUsedPeriod) this.selectedPeriod = this.lastUsedPeriod;
  }

  private downloadReportWithName(reportName: string): void {
    const tqi = this.metadata?.tableQueryIndex ?? 0;
    let reportQuery = '';
    if (this.isEditMode) {
      reportQuery = this.data?.reportQuery ?? this.data?.sql?.[tqi] ?? this.data?.sql?.[0] ?? '';
    } else if (this.executedSql && this.executedSql.length > 0) {
      reportQuery = this.executedSql[tqi] ?? this.executedSql[0];
    } else if (this.sql_statements && this.sql_statements.length > 0) {
      reportQuery = this.sql_statements[tqi] ?? this.sql_statements[0];
    }
    const params: any = {
      fleetId: this.fleetId,
      reportType: 'CUSTOM_REPORT',
      reportQuery,
      reportName,
      ...(this.hasValidReportSourceId ? { reportSourceId: this.getCurrentReportSourceId() } : {}),
    };
    this.reportService
      .downloadCustomReport(params)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.csvLoader = false;
        })
      )
      .subscribe({
        next: () => {
          this.snackBarService.success(this.translate.instant('Report downloaded successfully'));
        },
        error: (err: HttpErrorResponse) => {
          const key = err?.status === 409 ? 'tripListComponentTripDuplicate' : 'tripListComponentTripsDownloadFailure';
          this.snackBarService.failure(this.translate.instant(key));
        },
      });
  }

  private initializeEditMode(): void {
    if (!this.editReport) return;

    this.selectedPeriod = this.editReport.reportPayload?.dateRange ?? 'Past 7 days';
    this.calculateDateRange(this.selectedPeriod);
    this.lastUsedPeriod = this.selectedPeriod;

    const savedTagIds = this.editReport.reportPayload?.tagIds ?? [];
    if (savedTagIds.length > 0) {
      this.selectedTagIds = [...savedTagIds];
      this.filterSelectedTagIds = [...savedTagIds];
      this.lastUsedTagIds = [...savedTagIds];
      this.selectedTagNames = this.getTagNamesByIds(savedTagIds);
      this.lastUsedTagNames = [...this.selectedTagNames];
      this.userTags = this.getTagObjectsByIds(savedTagIds);
      this.assignedTagList = [...savedTagIds];
    }

    if (this.reportQuery) {
      this.sql = this.executedSql = this.sql_statements = [this.reportQuery];
    }

    this.conversationState = 'report_generated';
    this.isFirstQuery = false;
    this.loadExistingReport();
  }

  private loadExistingReport(): void {
    if (!this.editReport) return;

    this.isLoading = true;
    this.showNoDataMessage = false;

    this.reportService
      .getCustomReportData(this.customReportId, {
        reportQuery: this.reportQuery ?? '',
        ...(this.hasValidReportSourceId ? { reportSourceId: this.getCurrentReportSourceId() } : {}),
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (response: { rows: ReportDataRow[]; totalCount: number }) => {
          this.processEditModeResponse(response);
        },
        error: () => this.loadFromSavedData(),
      });
  }

  private processEditModeResponse(response: { rows: ReportDataRow[]; totalCount: number }): void {
    const rows = response?.rows;

    if (!rows?.length) {
      this.showNoDataMessage = true;
      this.dataSource = [];
      this.displayedColumns = [];
      this.addBotMessage(
        this.translate.instant(
          'The report loaded but returned no data for the saved filters. You can adjust the period or tags and regenerate.'
        )
      );
      return;
    }

    this.dataSource = rows;
    this.displayedColumns = Object.keys(rows[0]);
    this.showNoDataMessage = false;
    this.showNoDataRetrySection = false;
    this.conversationState = 'report_generated';
    this.addBotMessage(
      this.translate.instant(
        'Here is your saved report. You can type a new question below to modify it, or use the filters on the right to adjust the period and tags.'
      )
    );
  }

  private loadFromSavedData(): void {
    if (!this.editReport) return;

    const savedData = this.editReport.reportPayload?.data ?? [];
    const savedColumns = this.editReport.reportPayload?.columns ?? [];

    if (savedData.length > 0) {
      this.dataSource = savedData;
      this.displayedColumns = savedColumns.length > 0 ? savedColumns : Object.keys(savedData[0]);
      this.showNoDataMessage = false;
      this.showNoDataRetrySection = false;
      this.conversationState = 'report_generated';
      this.addBotMessage(
        this.translate.instant(
          'Here is your saved report. You can type a new question below to modify it, or use the filters on the right to adjust the period and tags.'
        )
      );
    } else {
      this.showNoDataMessage = true;
      this.dataSource = [];
      this.displayedColumns = [];
      this.addBotMessage(
        this.translate.instant('Could not load the saved report data. Please type a question below to generate a new report.')
      );
    }
  }

  private getCurrentDomain(): ReportDomain {
    return (
      this.metadata?.domain ||
      this.data?.domain ||
      (this.editReport as any)?.reportMetadata?.domain ||
      this.editReport?.reportPayload?.metadata?.domain ||
      'trip'
    );
  }

  public getCurrentReportSourceId(): number | null {
    const sourceId =
      this.metadata?.reportSourceId ||
      this.data?.reportSourceId ||
      (this.editReport as any)?.reportMetadata?.reportSourceId ||
      this.editReport?.reportPayload?.metadata?.reportSourceId ||
      null;

    return Number.isInteger(sourceId) ? Number(sourceId) : null;
  }

  public getCurrentCrossDomainPlan(): any {
    return (
      this.metadata?.crossDomainPlan ??
      this.data?.crossDomainPlan ??
      (this.editReport as any)?.reportMetadata?.crossDomainPlan ??
      this.editReport?.reportPayload?.metadata?.crossDomainPlan ??
      null
    );
  }

  public get hasValidReportSourceId(): boolean {
    return this.getCurrentReportSourceId() !== null;
  }

  public get isCrossDomain(): boolean {
    return this.getCurrentDomain() === 'cross_domain';
  }

  public get isWorkflowAwaitingInput(): boolean {
    const hasSql = this.getCurrentSqlStatements().length > 0;
    const hasData = Array.isArray(this.dataSource) && this.dataSource.length > 0;
    return this.getCurrentDomain() === 'workflow' && !hasSql && !hasData;
  }

  public get isActionControlsDisabled(): boolean {
    return this.isWorkflowAwaitingInput || this.isCrossDomain;
  }

  private toSqlArray(sqlValue: unknown): string[] {
    if (Array.isArray(sqlValue)) {
      return sqlValue.filter((sql) => typeof sql === 'string' && sql.trim().length > 0);
    }
    if (typeof sqlValue === 'string' && sqlValue.trim().length > 0) {
      return [sqlValue];
    }
    return [];
  }

  private getCurrentSqlStatements(): string[] {
    if (this.isEditMode) {
      const editModeSql = this.toSqlArray(this.data?.sql);
      if (editModeSql.length) {
        return editModeSql;
      }
    }

    const generatedSql = this.toSqlArray(this.sql_statements);
    if (generatedSql.length) {
      return generatedSql;
    }

    const executedSql = this.toSqlArray(this.executedSql);
    if (executedSql.length) {
      return executedSql;
    }

    return this.toSqlArray(this.sql);
  }

  private getSqlPayloadForLlm(sqlStatements: string[]): string | string[] {
    if (this.getCurrentDomain() === 'cross_domain') return sqlStatements;
    const tqi = this.metadata?.tableQueryIndex ?? 0;
    return sqlStatements[tqi] ?? sqlStatements[0] ?? '';
  }
}
